import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, Spinner } from "@/components/Common";
import { api, errMsg } from "@/lib/api";

const TIERS = ["free", "pro", "elite"];
export const DEFAULT_SETTINGS = {
  ai_monthly_budget: 1500, infra_monthly: 0, other_monthly: 0, stripe_fee_pct: 2.9, stripe_fee_fixed: 0.3, alert_at_pct: 80,
  limits: { free: { daily_ai_calls: 5, monthly_ai_cost_cap: 0.5 }, pro: { daily_ai_calls: 100, monthly_ai_cost_cap: 6 }, elite: { daily_ai_calls: 300, monthly_ai_cost_cap: 15 } },
};

const Field = ({ label, value, onChange, prefix, suffix, testid }) => (
  <label className="block">
    <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">{label}</span>
    <div className="flex items-center gap-1.5">{prefix && <span className="text-sm text-zinc-500">{prefix}</span>}
      <Input type="number" min="0" step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className="h-9 bg-[#09090B]" data-testid={testid} />
      {suffix && <span className="shrink-0 text-xs text-zinc-500">{suffix}</span>}</div>
  </label>
);

export const CostSettings = ({ initial, onSaved }) => {
  const [s, setS] = useState(DEFAULT_SETTINGS);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (initial) setS({ ...DEFAULT_SETTINGS, ...initial, limits: { ...DEFAULT_SETTINGS.limits, ...(initial.limits || {}) } }); }, [initial]);
  const set = (k) => (v) => setS((x) => ({ ...x, [k]: v }));
  const setLimit = (t, k) => (v) => setS((x) => ({ ...x, limits: { ...x.limits, [t]: { ...x.limits[t], [k]: v } } }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/cc/cost-settings", s); toast.success("Cost settings saved"); onSaved?.(data?.limits ? data : s); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <Card data-testid="cost-settings-card">
        <h3 className="font-display text-lg text-zinc-100">Monthly costs</h3>
        <p className="mb-4 mt-1 text-xs text-zinc-500">Used to allocate shared costs per user. AI usage per user and Stripe fees come from real data; the fee rate below is used only when Stripe's actual fee is missing.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="AI budget / month" prefix="$" value={s.ai_monthly_budget} onChange={set("ai_monthly_budget")} testid="cost-ai-budget" />
          <Field label="Infrastructure / month" prefix="$" value={s.infra_monthly} onChange={set("infra_monthly")} testid="cost-infra" />
          <Field label="Other tools / month" prefix="$" value={s.other_monthly} onChange={set("other_monthly")} testid="cost-other" />
          <Field label="Alert at % of AI budget" suffix="%" value={s.alert_at_pct} onChange={set("alert_at_pct")} testid="cost-alert-pct" />
          <Field label="Stripe fee rate" suffix="%" value={s.stripe_fee_pct} onChange={set("stripe_fee_pct")} testid="cost-stripe-pct" />
          <Field label="Stripe fixed fee" prefix="$" value={s.stripe_fee_fixed} onChange={set("stripe_fee_fixed")} testid="cost-stripe-fixed" />
        </div>
      </Card>
      <Card data-testid="usage-limits-card">
        <h3 className="font-display text-lg text-zinc-100">AI usage safeguards</h3>
        <p className="mb-4 mt-1 text-xs text-zinc-500">Per-user caps by plan. When a user hits a cap, the app asks them to wait or upgrade. Leave blank for no cap.</p>
        <div className="space-y-3">
          {TIERS.map((t) => (
            <div key={t} className="grid grid-cols-[60px_1fr_1fr] items-end gap-3">
              <span className="pb-2 text-xs font-semibold uppercase tracking-wider text-[#F3C649]">{t}</span>
              <Field label="AI calls / day" value={s.limits[t]?.daily_ai_calls} onChange={setLimit(t, "daily_ai_calls")} testid={`limit-${t}-daily`} />
              <Field label="AI cost cap / month" prefix="$" value={s.limits[t]?.monthly_ai_cost_cap} onChange={setLimit(t, "monthly_ai_cost_cap")} testid={`limit-${t}-cap`} />
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={busy} className="mt-5 w-full rounded-full" data-testid="cost-settings-save">{busy ? <Spinner /> : "Save costs & limits"}</Button>
      </Card>
    </div>
  );
};
